import produce from 'immer';
import {
  IQuestionnaire,
  IQuestionnaireItemGroupComponent,
  IQuestionnaireItemTableComponent,
  QuestionnaireItemComponent,
  QuestionnaireItemEnableWhenComponent,
} from '../../../unsw-fhir/Questionnaire';
import { isHidden } from '../../../unsw-fhir/Questionnaire/extensions';
import {
  isQuestionnaire,
  isQuestionnaireGroupItem,
  isQuestionnaireOptionCoding,
  isQuestionnaireOptionDate,
  isQuestionnaireOptionInteger,
  isQuestionnaireOptionString,
  isQuestionnaireOptionTime,
  isQuestionnaireQuestionItem,
  isQuestionnaireTableGroupItem,
} from '../../../unsw-fhir/Questionnaire/typeGuards';
import {
  IQuestionnaireResponse,
  IQuestionnaireResponseItemAnswerComponent,
  IQuestionnaireResponseItemAnswerTableComponent,
  IQuestionnaireResponseItemGroupComponent,
  QuestionnaireResponseAnswerComponent,
  QuestionnaireResponseItemComponent,
} from '../../../unsw-fhir/QuestionnaireResponse';
import {
  isQuestionnnaireResponseItemDeepEqual,
  isQuestionnnaireResponseItemEqual,
} from '../../../unsw-fhir/QuestionnaireResponse/comparator';
import { generateEnableWhenPredicate } from '../../../unsw-fhir/QuestionnaireResponse/helpers';
import {
  isQuestionnaireResponse,
  isQuestionnaireResponseItemAnswer,
  isQuestionnaireResponseItemGroup,
  isQuestionnaireResponseItemTableGroup,
} from '../../../unsw-fhir/QuestionnaireResponse/typeGuards';
import { createQuestionnaireReference } from '../../../unsw-fhir/Reference';
import { ISectionItem } from '../../Contexts';
// Types & Interfaces

interface IContainer<T> {
  value: T;
  path: string[];
}
type IEnableWhenContainer = QuestionnaireItemEnableWhenComponent & {
  is: (v: QuestionnaireResponseItemComponent | undefined | null) => boolean;
};

// Utlities
function isStringArray(value: any): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

// Logic to remove objects from the tree
const rec_removeIf = (
  response: QuestionnaireResponseItemComponent,
  pred: (value: QuestionnaireResponseItemComponent) => boolean,
): QuestionnaireResponseItemComponent | null => {
  if (pred(response)) return null;

  if (isQuestionnaireResponseItemAnswer(response)) {
    return response;
  }

  return produce(response, draft => {
    draft.item = removeIf(draft.item, pred);
  });
};

const removeIf = (
  responses: QuestionnaireResponseItemComponent[],
  pred: (value: QuestionnaireResponseItemComponent) => boolean,
): QuestionnaireResponseItemComponent[] => {
  const filtered = responses
    .map(item => rec_removeIf(item, pred))
    .filter(
      (item): item is QuestionnaireResponseItemComponent => item !== null,
    );
  return filtered;
};

const removeResponses = (
  responses: IQuestionnaireResponse,
  linkIds: string[],
): IQuestionnaireResponse => {
  return produce(responses, draft => {
    draft.item = removeIf(responses.item, ans => linkIds.includes(ans.linkId));
    return draft;
  });
};

// Logic to walk the response tree and callback on each item;
const walkQuestionnaireResponseTableGroupTree = (
  resource: IQuestionnaireResponseItemAnswerTableComponent,
  pred: (value: QuestionnaireResponseItemComponent, path: string[]) => void,
  path: string[] = [],
): void => {
  pred(resource, path);
  if (!resource.item) return;
  resource.item.forEach(item =>
    walkResponseTree(item, pred, [...path, resource.linkId]),
  );
};

const walkResponseTree = (
  response: QuestionnaireResponseItemComponent | IQuestionnaireResponse,
  pred: (value: QuestionnaireResponseItemComponent, path: string[]) => void,
  path: string[] = [],
): void => {
  if (isQuestionnaireResponse(response)) {
    return response.item.forEach(item => walkResponseTree(item, pred));
  }
  if (isQuestionnaireResponseItemTableGroup(response)) {
    pred(response, path);
    return response.item.forEach(item =>
      walkQuestionnaireResponseTableGroupTree(item, pred, [
        ...path,
        response.linkId,
      ]),
    );
  }
  if (isQuestionnaireResponseItemGroup(response)) {
    response.item.forEach(item =>
      walkResponseTree(item, pred, [...path, response.linkId]),
    );
  }
  pred(response, path);
};

// Logic to walk the questionnaire tree and callback on each item;
const walkQuestionnaireTableGroupTree = (
  resource: IQuestionnaireItemTableComponent,
  pred: (value: QuestionnaireItemComponent, path: string[]) => void,
  path: string[] = [],
): void => {
  pred(resource, path);
  if (!resource.item) return;
  resource.item.forEach(item =>
    walkQuestionnaireTree(item, pred, [...path, resource.linkId]),
  );
};

const walkQuestionnaireTree = (
  resource: QuestionnaireItemComponent | IQuestionnaire,
  pred: (value: QuestionnaireItemComponent, path: string[]) => void,
  path: string[] = [],
): void => {
  if (isQuestionnaire(resource)) {
    return resource.item.forEach(item => walkQuestionnaireTree(item, pred));
  }
  if (isQuestionnaireTableGroupItem(resource)) {
    pred(resource, path);
    return resource.item.forEach(item =>
      walkQuestionnaireTableGroupTree(item, pred, [...path, resource.linkId]),
    );
  }
  if (isQuestionnaireGroupItem(resource)) {
    resource.item.forEach(item =>
      walkQuestionnaireTree(item, pred, [...path, resource.linkId]),
    );
  }
  pred(resource, path);
};

// Create Answer Item
const createAnswerItem = (
  value: string,
  question: QuestionnaireItemComponent,
): QuestionnaireResponseAnswerComponent => {
  if (question.type === 'string' || question.type === 'text') {
    return {
      valueString: value,
    };
  }
  if (question.type === 'date') {
    return {
      valueDate: value,
    };
  }
  if (question.type === 'integer') {
    return {
      valueInteger: Number.parseInt(value),
    };
  }
  if (question.type === 'decimal') {
    return {
      valueDecimal: Number.parseFloat(value),
    };
  }
  if (question.type === 'boolean') {
    return {
      valueBoolean: value.toLowerCase() === 'true',
    };
  }

  if (question.type === 'choice' || question.type === 'open-choice') {
    const item = question.option
      ? question.option.find(x => {
          if (isQuestionnaireOptionCoding(x) && x.valueCoding.code === value)
            return x.valueCoding;
          if (
            isQuestionnaireOptionInteger(x) &&
            x.valueInteger.toString() === value
          )
            return x.valueInteger;
          if (isQuestionnaireOptionString(x) && x.valueString === value)
            return x.valueString;
          if (isQuestionnaireOptionTime(x) && x.valueTime === value)
            return x.valueTime;
          if (isQuestionnaireOptionDate(x) && x.valueDate === value)
            return x.valueDate;
          return undefined;
        })
      : undefined;

    if (!item) throw new Error('Could not find options for answer');
    return item;
  }
  throw new Error(
    `Could not create response Item from question type ${question.type}`,
  );
};

const createAnswerItems = (
  value: string | string[],
  question: QuestionnaireItemComponent,
  response: IQuestionnaireResponseItemAnswerComponent,
): IQuestionnaireResponseItemAnswerComponent => {
  if (isStringArray(value)) {
    if (value.length === 0) return response;
    return produce(response, draft => {
      value.forEach(v => draft.answer.push(createAnswerItem(v, question)));
      return draft;
    });
  }

  return produce(response, draft => {
    draft.answer.push(createAnswerItem(value, question));
    return draft;
  });
};

const createQuestionnaireReponseItem = (
  value: string | string[],
  question: QuestionnaireItemComponent,
): IQuestionnaireResponseItemAnswerComponent => {
  const response: IQuestionnaireResponseItemAnswerComponent = {
    text: question.text,
    linkId: question.linkId,
    extension: question.extension,
    answer: [],
  };
  if ((question as any).item) {
    (response as any).item = [];
  }
  return produce(response, draft => {
    return createAnswerItems(value, question, draft);
  });
};

// Logic to update the response tree
const setResponseItem = (
  resouce: IQuestionnaireResponseItemGroupComponent | IQuestionnaireResponse,
  item: QuestionnaireResponseItemComponent,
) => {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  const index = resouce.item.findIndex(value =>
    isQuestionnnaireResponseItemEqual(value, item),
  );

  if (index < 0) resouce.item.push(item);
  else {
    if (!isQuestionnnaireResponseItemDeepEqual(resouce.item[index], item)) {
      resouce.item[index] = item;
    }
  }

  resouce.item = resouce.item.sort((a, b) =>
    collator.compare(a.linkId, b.linkId),
  );
  return resouce;
};

const setResponseRecursive = (
  item: IQuestionnaireResponseItemAnswerComponent,
  node: IQuestionnaireResponseItemGroupComponent | IQuestionnaireResponse,
  questionnaireItemMap: Map<string, IContainer<QuestionnaireItemComponent>>,
  path: string[],
) =>
  produce(node, draft => {
    const [current, ...rest] = path;
    if (!current) {
      const retV = setResponseItem(draft, item);
      return retV;
    }

    const gContainer = questionnaireItemMap.get(current);
    if (!gContainer) throw new Error('Could not find item in map');
    const responseGroupItem = {
      linkId: gContainer.value.linkId,
      text: gContainer.value.text,
      item: [],
      extension: gContainer.value.extension,
    };

    draft = setResponseItem(draft, responseGroupItem);

    const index = draft.item.findIndex(value => value.linkId === current);
    if (index < 0 || !draft.item[index])
      throw new Error('could not find item that, it was just added');

    draft.item[index] = setResponseRecursive(
      item,
      draft.item[index] as IQuestionnaireResponseItemGroupComponent,
      questionnaireItemMap,
      rest,
    ) as QuestionnaireResponseItemComponent;

    return draft;
  });

const setResponse = (
  linkId: string,
  value: string | string[],
  response: IQuestionnaireResponse,
  questionnaireItemMap: Map<string, IContainer<QuestionnaireItemComponent>>,
) => {
  const container = questionnaireItemMap.get(linkId);
  if (!container) throw new Error('could not find conatiner for question');
  const item = createQuestionnaireReponseItem(value, container.value);

  return setResponseRecursive(
    item,
    response,
    questionnaireItemMap,
    container.path,
  );
};

const updateQuestionnaireResponse = (
  response: IQuestionnaireResponse,
  values: { [index: string]: string[] | string },
  questionnaireItemMap: Map<string, IContainer<QuestionnaireItemComponent>>,
) => {
  return produce(response, draft => {
    Object.entries(values).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length === 0) return;
      draft = setResponse(
        key,
        value,
        draft,
        questionnaireItemMap,
      ) as IQuestionnaireResponse;
    });
    return draft;
  });
};

const generateResponseMap = (response?: IQuestionnaireResponse | null) => {
  const map: Map<
    string,
    IContainer<QuestionnaireResponseItemComponent>
  > = new Map();
  if (!response) return map;
  walkResponseTree(response, (value, path) => {
    map.set(value.linkId, {
      value,
      path,
    });
  });
  return map;
};

const genereateQuestionnaireMaps = (
  questionnaire: IQuestionnaire,
): [
  Map<string, IContainer<QuestionnaireItemComponent>>,
  Map<string, IEnableWhenContainer[]>,
] => {
  const enableWhenMap: Map<string, IEnableWhenContainer[]> = new Map();
  const questionnaireItemMap: Map<
    string,
    IContainer<QuestionnaireItemComponent>
  > = new Map();

  walkQuestionnaireTree(questionnaire, (value, path) => {
    questionnaireItemMap.set(value.linkId, {
      value,
      path,
    });

    if (value.enableWhen) {
      value.enableWhen.forEach(v => {
        const enableWhens =
          enableWhenMap.get(value.linkId) || ([] as IEnableWhenContainer[]);

        enableWhens.push({ ...v, is: generateEnableWhenPredicate(v) });
        enableWhenMap.set(value.linkId, enableWhens);
      });
    }
  });
  return [questionnaireItemMap, enableWhenMap];
};

export default class QuestionnaireMutator {
  questionnaire: IQuestionnaire;
  responses?: IQuestionnaireResponse | null;
  sections: ISectionItem[];
  // cache maps for the indexing into the graphs
  private responseItemMap: Map<
    string,
    IContainer<QuestionnaireResponseItemComponent>
  >;
  private questionnaireItemMap: Map<
    string,
    IContainer<QuestionnaireItemComponent>
  >;
  private enableWhenMap: Map<string, IEnableWhenContainer[]>;

  generateSectionsState = (
    questionnaireItemMap: Map<string, IContainer<QuestionnaireItemComponent>>,
  ) => {
    const sections = Array.from(questionnaireItemMap)
      .map(x => x[1])
      .filter(x => x.path.length === 0)
      .map(x => x.value);
    return sections.map(x => ({
      linkId: x.linkId,
      text: x.text || '',
      enabled: this.isEnabled(x.linkId),
      complete: this.isComplete(x.linkId),
    }));
  };

  private computeProgress = (linkId?: string | null): [number, number] => {
    if (!linkId) {
      const completions = this.questionnaire.item.map(v =>
        this.computeProgress(v.linkId),
      );
      const sum = completions.reduce(
        (pv, cv) => [pv[0] + cv[0], pv[1] + cv[1]],
        [0, 0],
      );
      return sum;
    }

    const questionnaireItem = this.questionnaireItemMap.get(linkId);
    if (!questionnaireItem)
      throw new Error(`Unable to find questionnaire item: ${linkId}`);

    const { value: question } = questionnaireItem;
    if (isQuestionnaireGroupItem(question)) {
      const rQuestions = new Array<string>();
      walkQuestionnaireTree(question, item => {
        if (!isQuestionnaireQuestionItem(item)) return;
        if (item.required) rQuestions.push(item.linkId);
      });
      const rFiltered = rQuestions.filter(v => this.isEnabled(v));
      const rCompleted = rFiltered
        .map(v => this.isComplete(v))
        .filter(i => !!i);
      return [rCompleted.length, rFiltered.length];
    } else {
      return question.required
        ? [this.isComplete(question.linkId) ? 1 : 0, 1]
        : [0, 0];
    }
  };

  constructor(
    questionnaire: IQuestionnaire,
    response?: IQuestionnaireResponse,
  ) {
    this.questionnaire = questionnaire;
    this.responses = response;
    this.sections = [];
    this.responseItemMap = generateResponseMap(this.responses);
    [this.questionnaireItemMap, this.enableWhenMap] =
      genereateQuestionnaireMaps(this.questionnaire);
    this.sections = this.generateSectionsState(this.questionnaireItemMap);
  }
  goPrevious = (
    itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire,
    itemIndex: number,
  ): [QuestionnaireItemComponent | IQuestionnaire | null, number] => {
    let nIndex = itemIndex;
    let nViewItem: QuestionnaireItemComponent | null = null;
    do {
      nIndex -= 1;
      if (nIndex <= 0 && !!itemNode.item[nIndex]) {
        nViewItem = null;
      }
      nViewItem = itemNode.item[nIndex];
    } while (nViewItem !== null && !this.isEnabled(nViewItem.linkId));

    return [nViewItem, nIndex];
  };

  goNext = (
    itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire,
    itemIndex: number,
  ): [QuestionnaireItemComponent | IQuestionnaire | null, number] => {
    let nIndex = itemIndex;
    let nViewItem: QuestionnaireItemComponent | null = null;
    do {
      nIndex += 1;
      if (nIndex >= itemNode.item.length || !!itemNode.item[nIndex]) {
        nViewItem = null;
      }
      nViewItem = itemNode.item[nIndex];
    } while (nViewItem !== null && !this.isEnabled(nViewItem.linkId));

    return [nViewItem, nIndex];
  };

  getProgress = (linkId?: string | null): number => {
    const [completed, total] = this.computeProgress(linkId);
    if (total <= 0) return 100;
    return (completed / total) * 100;
  };

  isEnabled = (linkId: string) => {
    const question = this.questionnaireItemMap.get(linkId);
    if (question) {
      const hidden = isHidden(question.value);
      if (hidden) return false;
    }

    const enableWhens = this.enableWhenMap.get(linkId);
    if (!enableWhens) return true;

    return enableWhens.reduce((pv, cv) => {
      const response = this.responseItemMap.get(cv.question);
      if (!response) {
        return pv || cv.is(response);
      }
      return pv || cv.is(response.value);
    }, false);
  };

  isComplete = (linkId?: string | null): boolean => {
    if (!linkId)
      return this.questionnaire.item.every(v => this.isComplete(v.linkId));

    const questionnaireItem = this.questionnaireItemMap.get(linkId);
    if (!questionnaireItem)
      throw new Error(`Unable to find questionnaire item: ${linkId}`);
    const response = this.responseItemMap.get(linkId);

    if (!response) return false;
    if (isQuestionnaireGroupItem(questionnaireItem.value)) {
      const rQuestions = new Array<string>();
      const oQuestions = new Array<string>();
      walkQuestionnaireTree(questionnaireItem.value, value => {
        if (!isQuestionnaireQuestionItem(value)) return;
        if (value.required) rQuestions.push(value.linkId);
        else oQuestions.push(value.linkId);
      });
      const rCompleted = rQuestions
        .filter(v => this.isEnabled(v))
        .every(v => this.isComplete(v));
      const oCompleted = oQuestions.some(v => this.isComplete(v));
      return rCompleted && (rCompleted || oCompleted);
    }

    return true;
  };

  enforceConstraints = (
    response: IQuestionnaireResponse,
  ): IQuestionnaireResponse => {
    if (!response) return response;
    const removeIds: string[] = [];
    const map = generateResponseMap(response);
    this.enableWhenMap.forEach((_, key) => {
      const isDisabled = !!map.get(key) && !this.isEnabled(key);
      if (isDisabled) removeIds.push(key);
    });
    if (removeIds.length > 0) {
      const nResp = removeResponses(response, removeIds);
      return this.enforceConstraints(nResp);
    }
    return response;
  };

  updateResponse = (response?: IQuestionnaireResponse | null) => {
    this.responses = response;
    this.responseItemMap = generateResponseMap(response);
    this.sections = this.generateSectionsState(this.questionnaireItemMap);
  };

  setResponses = (values: { [index: string]: [string] | string }) => {
    let lResponse: IQuestionnaireResponse | null | undefined = {
      id: this.responses ? this.responses.id : undefined,
      questionnaire: createQuestionnaireReference(
        this.questionnaire.id || '-1',
      ),
      resourceType: 'QuestionnaireResponse',
      item: [],
    };

    return updateQuestionnaireResponse(
      lResponse,
      values,
      this.questionnaireItemMap,
    );
  };

  addResponse = (values: { [index: string]: [string] | string }) => {
    if (!this.responses) return undefined;

    return updateQuestionnaireResponse(
      this.responses,
      values,
      this.questionnaireItemMap,
    );
  };

  getFirst = (linkId?: string) => {
    if (linkId) throw new Error('Getting first by LinkId not Supported yet');
    return {
      itemView: this.questionnaire.item[0],
      itemNode: this.questionnaire,
      itemIndex: 0,
    };
  };

  isLast = (
    itemView: IQuestionnaire | QuestionnaireItemComponent | null,
    itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire,
    itemIndex: number,
  ) => {
    const index = itemIndex + 1;
    if (isQuestionnaire(itemView)) return true;
    if (index >= itemNode.item.length) return true;
    const hasNextItem = itemNode.item
      .slice(index)
      .reduce((pv, cv) => pv || this.isEnabled(cv.linkId), false);
    return !hasNextItem;
  };

  isFirst = (
    itemView: IQuestionnaire | QuestionnaireItemComponent | null,
    itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire,
    itemIndex: number,
  ) => {
    const index = itemIndex - 1;
    if (isQuestionnaire(itemView)) return true;
    if (index < 0) return true;
    const hasNextItem = itemNode.item
      .slice(0, itemIndex)
      .reduce((pv, cv) => pv || this.isEnabled(cv.linkId), false);
    return !hasNextItem;
  };

  getQuestionnaireResponse = (linkId: string) =>
    this.responseItemMap.get(linkId);

  getQuestionnaireResponses = () => this.responseItemMap;

  getQuestionnaireItems = () => this.questionnaireItemMap;
}
